import {
  Box,
  CircularProgress,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { useTenantTranslation } from "hooks/formatters";
import { useAvailableTenantMetadataContext } from "contexts/availableTenantMetadata";
import {
  DateRangeProvider,
  useDateRange,
  type DateRangeConfig,
} from "contexts/DateRangeContext";
import { DateRangeSelector } from "components/common/DateRangeSelector";
import { MultiSelectAutocomplete } from "components/common/MultiSelectAutocomplete";
import { sorted } from "helpers";
import { DEFAULT_DESELECTED_TENANT_NAMES } from "hooks/useScrapEyeTenantSelection";
import { useCallback, useMemo, useState } from "react";
import type { GetLabellingCostDailyApiArg } from "src/store/api/generatedApi";
import { formatTimestampWithOffset } from "src/utils/formatTimestampWithOffset";
import { CombinedCostRatioChart } from "./CombinedCostRatioChart";
import { CombinedCostSummary } from "./CombinedCostSummary";
import { CombinedDailyCostChart } from "./CombinedDailyCostChart";
import { TenantCostCard } from "./TenantCostCard";

type CostDateGrouping = NonNullable<GetLabellingCostDailyApiArg["groupBy"]>;

const LABELLING_COST_DATE_RANGE_CONFIG: DateRangeConfig = {
  paramPrefix: "lc",
  defaultStartDate: dayjs().subtract(30, "day"),
  defaultEndDate: dayjs(),
};

const LabellingCostOverviewContent = () => {
  const { t } = useTenantTranslation();
  const { tenantMetadataList, isLoading } = useAvailableTenantMetadataContext();
  const { startDate, endDate } = useDateRange();

  const scrapEyeTenants = useMemo(
    () =>
      sorted(
        tenantMetadataList.filter((tenant) =>
          tenant.feature_flags.includes("scrap_eyes")
        ),
        (tenant) => tenant.tenant_name
      ),
    [tenantMetadataList]
  );

  const [dateGrouping, setDateGrouping] =
    useState<CostDateGrouping>("footage_date");

  type TenantOption = Readonly<{ id: string; longName: string }>;

  const tenantOptions: readonly TenantOption[] = useMemo(
    () =>
      scrapEyeTenants.map((tenant) => ({
        id: tenant.tenant_name,
        longName: tenant.long_name,
      })),
    [scrapEyeTenants]
  );

  const defaultSelectedTenantIds = useMemo(
    () =>
      scrapEyeTenants
        .filter(
          (tenant) =>
            !DEFAULT_DESELECTED_TENANT_NAMES.includes(tenant.tenant_name)
        )
        .map((tenant) => tenant.tenant_name),
    [scrapEyeTenants]
  );

  const [selectedTenantIds, setSelectedTenantIds] = useState<
    readonly string[] | null
  >(null);

  const activeTenantIds = selectedTenantIds ?? defaultSelectedTenantIds;

  const handleTenantSelectionChange = useCallback((ids: readonly string[]) => {
    setSelectedTenantIds(ids);
  }, []);

  const filterTenantOption = useCallback(
    (option: TenantOption, input: string) => {
      const query = input.toLowerCase();
      return (
        option.id.toLowerCase().includes(query) ||
        option.longName.toLowerCase().includes(query)
      );
    },
    []
  );

  const activeTenantDescriptors = useMemo(() => {
    const activeSet = new Set(activeTenantIds);
    return scrapEyeTenants
      .filter((tenant) => activeSet.has(tenant.tenant_name))
      .map((tenant) => ({
        tenantName: tenant.tenant_name,
        tenantLongName: tenant.long_name,
      }));
  }, [scrapEyeTenants, activeTenantIds]);

  const start = formatTimestampWithOffset(startDate);
  const end = formatTimestampWithOffset(endDate);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3} sx={{ p: 3 }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="h4" sx={{ flex: 1 }}>
          {t("labellingCostOverview")}
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={dateGrouping}
          onChange={(_, value: CostDateGrouping | null) => {
            if (value) setDateGrouping(value);
          }}
        >
          <ToggleButton value="session_date">
            {t("groupBySessionDate")}
          </ToggleButton>
          <ToggleButton value="footage_date">
            {t("groupByFootageDate")}
          </ToggleButton>
        </ToggleButtonGroup>
        <DateRangeSelector />
      </Stack>

      <MultiSelectAutocomplete
        options={tenantOptions}
        selectedIds={activeTenantIds}
        onChange={handleTenantSelectionChange}
        getOptionLabel={(option) => option.longName}
        filterOption={filterTenantOption}
        label={t("tenants")}
        chipPlacement="below"
      />

      {activeTenantDescriptors.length > 0 && (
        <Stack spacing={2}>
          <CombinedCostSummary
            tenants={activeTenantDescriptors}
            start={start}
            end={end}
            groupBy={dateGrouping}
          />
          <Box
            sx={{
              p: 2,
              backgroundColor: "grey.50",
              borderRadius: 2,
              border: 1,
              borderColor: "grey.300",
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} sx={{ pb: 1 }}>
              {t("labellingCostComparison")}
            </Typography>
            <CombinedCostRatioChart
              tenants={activeTenantDescriptors}
              start={start}
              end={end}
              groupBy={dateGrouping}
              metricKey="labelling_cost_ratio"
            />
          </Box>
          <Box
            sx={{
              p: 2,
              backgroundColor: "grey.50",
              borderRadius: 2,
              border: 1,
              borderColor: "grey.300",
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} sx={{ pb: 1 }}>
              {t("reviewCostComparison")}
            </Typography>
            <CombinedCostRatioChart
              tenants={activeTenantDescriptors}
              start={start}
              end={end}
              groupBy={dateGrouping}
              metricKey="review_cost_ratio"
            />
          </Box>
          <Box
            sx={{
              p: 2,
              backgroundColor: "grey.50",
              borderRadius: 2,
              border: 1,
              borderColor: "grey.300",
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} sx={{ pb: 1 }}>
              {t("dailyCostComparison")}
            </Typography>
            <CombinedDailyCostChart
              tenants={activeTenantDescriptors}
              start={start}
              end={end}
              groupBy={dateGrouping}
            />
          </Box>
        </Stack>
      )}

      <Stack spacing={2}>
        {activeTenantDescriptors.map((tenant) => (
          <TenantCostCard
            key={tenant.tenantName}
            tenantName={tenant.tenantName}
            tenantLongName={tenant.tenantLongName}
            start={start}
            end={end}
            groupBy={dateGrouping}
          />
        ))}
      </Stack>

      {scrapEyeTenants.length === 0 && (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography color="text.secondary">{t("noTenantsFound")}</Typography>
        </Box>
      )}
    </Stack>
  );
};

export const LabellingCostOverviewPage = () => (
  <DateRangeProvider config={LABELLING_COST_DATE_RANGE_CONFIG}>
    <LabellingCostOverviewContent />
  </DateRangeProvider>
);
